import { useState } from "react";
import { motion } from "framer-motion";
import { Card, ButtonComponent, SelectInput } from "../../../components/ui";
import { Upload, CheckCircle } from "lucide-react";
import { importDatabaseRecords } from "../../../controllers/databaseController";

export default function ManualImportData() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mappedColumns, setMappedColumns] = useState({});
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const steps = ["Upload File", "Map Columns", "Review & Import", "Complete"];

  // Custom CSV parser function that respects quotes
  const parseCSVLine = (line) => {
    const fields = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // Toggle quote state
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        // Only split on comma if we're NOT inside quotes
        fields.push(current.trim().replace(/^"|"$/g, "")); // Remove surrounding quotes
        current = "";
      } else {
        current += char;
      }
    }

    // Add the last field
    fields.push(current.trim().replace(/^"|"$/g, ""));
    return fields;
  };

  // Parse CSV or Excel
  const handleFile = async (selectedFile) => {
    setFile(selectedFile);

    const ext = selectedFile.name.split(".").pop().toLowerCase();

    if (ext === "csv") {
      const text = await selectedFile.text();
      const lines = text.split(/\r?\n/);
      const parsedRows = lines.map((line) => parseCSVLine(line));
      setColumns(parsedRows[0]);
      setPreview(parsedRows.slice(1));
    } else {
      const XLSX = await import("xlsx");
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      setColumns(json[0]);
      setPreview(json.slice(1));
    }

    setStep(2);
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setLoading(true);

      const response = await importDatabaseRecords(file, mappedColumns);
      setReport(response);

      setStep(4);
    } catch (error) {
      console.error(error);
      alert(
        error.response?.data?.message || "Import failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!mappedColumns["Name"]) {
      alert("Please select a Name before continuing.");
      return;
    }
    setStep(3);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex justify-end mb-2">
        <ButtonComponent
          variant="outline"
          onClick={() => {
            setStep(1);
            setFile(null);
            setColumns([]);
            setMappedColumns({});
            setPreview([]);
          }}
        >
          Reset
        </ButtonComponent>
      </div>
      {/* STEPPER */}
      <div className="grid grid-cols-4 gap-4">
        {steps.map((label, index) => {
          const active = step === index + 1;
          const completed = step > index + 1;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex flex-col items-center text-center p-3 rounded-xl border shadow-sm ${
                active
                  ? index === 3
                    ? "bg-green-50 border-green-400 dark:bg-green-500/10 dark:border-green-500/60"
                    : "bg-blue-50 border-blue-400 dark:bg-blue-500/10 dark:border-blue-500/60"
                  : completed
                    ? "bg-green-50 border-green-400 dark:bg-green-500/10 dark:border-green-500/60"
                    : "bg-gray-100 border-gray-300 dark:bg-slate-800 dark:border-slate-600"
              }`}
            >
              <div
                className={`w-8 h-8 mb-2 flex items-center justify-center rounded-full text-white ${
                  active
                    ? index === 3
                      ? "bg-green-500"
                      : "bg-blue-500"
                    : completed
                      ? "bg-green-500"
                      : "bg-gray-400 dark:bg-slate-600"
                }`}
              >
                {completed ? (
                  <CheckCircle size={18} />
                ) : index === 3 ? (
                  <CheckCircle size={18} />
                ) : (
                  index + 1
                )}
              </div>
              <p className="text-sm font-medium dark:text-slate-200">{label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* MAIN STEP CONTENT */}
      <Card className="rounded-2xl shadow-md">
        <div className="p-6">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-4 text-center"
            >
              <label className="w-full p-10 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition dark:border-slate-600 dark:hover:border-slate-500 dark:hover:bg-slate-800/60">
                <Upload size={40} className="text-gray-600 mb-3 dark:text-slate-400" />
                <p className="text-lg font-medium dark:text-slate-100">
                  Upload or Drag & Drop File
                </p>
                <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">
                  Supported: CSV, XLS, XLSX
                </p>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv, .xls, .xlsx"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </label>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-semibold dark:text-slate-100">Map Columns</h3>
              <div className="space-y-2">
                {columns.map((col, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border p-3 rounded-lg bg-gray-50 w-full dark:border-slate-700 dark:bg-slate-800/60"
                  >
                    <div className="flex items-center justify-between gap-4 w-2/3">
                      {/* Show the mapped column in <p> */}
                      <p className="font-medium whitespace-nowrap dark:text-slate-200">
                        {mappedColumns[col] || col}
                      </p>

                      <SelectInput
                        className="w-40"
                        value={mappedColumns[col] || ""}
                        onChange={(e) =>
                          setMappedColumns({
                            ...mappedColumns,
                            [col]: e.target.value,
                          })
                        }
                      >
                        <option value="">Select field</option>
                        {/* Dynamically show all columns as options */}
                        {columns.map((optionCol, i) => (
                          <option key={i} value={optionCol}>
                            {optionCol}
                          </option>
                        ))}
                      </SelectInput>
                    </div>

                    {/* RIGHT: Preview value */}
                    {mappedColumns[col] && preview[0] && (
                      <span className="text-xs text-gray-600 w-1/3 text-right truncate dark:text-slate-400">
                        {(() => {
                          const selected = mappedColumns[col];
                          const targetIndex = columns.findIndex(
                            (c) => c.toLowerCase() === selected.toLowerCase(),
                          );
                          return preview[0]?.[targetIndex] || "";
                        })()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4">
                <ButtonComponent variant="outline" onClick={() => setStep(1)}>
                  Back
                </ButtonComponent>
                <ButtonComponent onClick={handleNext}>Next</ButtonComponent>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-semibold dark:text-slate-100">Review & Import</h3>
              <p className="text-gray-500 text-sm dark:text-slate-400">Mapped Preview</p>
              <div className=" rounded p-4 bg-gray-50 overflow-auto max-h-96 dark:bg-slate-800/60">
                <table className="w-full text-sm dark:text-slate-200">
                  <thead>
                    <tr className="bg-gray-200 dark:bg-slate-700 dark:text-slate-100">
                      <th className="p-2 border text-left w-12 dark:border-slate-600">ID</th>
                      {Object.values(mappedColumns).map((val, i) => (
                        <th key={i} className="p-2 border text-left dark:border-slate-600">
                          {val}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-b dark:border-slate-700">
                        <td className="p-2 border text-gray-500 dark:border-slate-700 dark:text-slate-400">{i + 1}</td>
                        {Object.values(mappedColumns).map((val, j) => {
                          const colIndex = columns.findIndex(
                            (c) => c.toLowerCase() === val.toLowerCase(),
                          );
                          return (
                            <td key={j} className="p-2 border dark:border-slate-700">
                              {row[colIndex] || ""}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between mt-4">
                <ButtonComponent variant="outline" onClick={() => setStep(2)}>
                  Back
                </ButtonComponent>
                <ButtonComponent disabled={loading} onClick={handleImport}>
                  {loading ? "Importing..." : "Import"}
                </ButtonComponent>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-3 py-10"
            >
              <CheckCircle size={50} className="text-green-600 dark:text-green-400" />
              <h3 className="text-2xl font-semibold text-green-600 dark:text-green-400">
                Import Complete
              </h3>
              <p className="text-gray-500 text-sm dark:text-slate-400">
                Your data has been successfully imported.
              </p>

              <div className="grid grid-cols-2 gap-4 mt-6 w-full max-w-md text-left">
                <div className="p-4 bg-gray-100 rounded-xl dark:bg-slate-800">
                  <p className="text-sm text-gray-500 dark:text-slate-400">Total Records</p>
                  <p className="text-xl font-semibold dark:text-slate-100">{preview.length}</p>
                </div>
                <div className="p-4 bg-green-100 rounded-xl dark:bg-green-500/15">
                  <p className="text-sm text-gray-500 dark:text-slate-400">Successfully Imported</p>
                  <p className="text-xl font-semibold text-green-600 dark:text-green-400">
                    {report.imported || 0}
                  </p>
                </div>
                <div className="p-4 bg-red-100 rounded-xl dark:bg-red-500/15">
                  <p className="text-sm text-gray-500 dark:text-slate-400">Errors</p>
                  <p className="text-xl font-semibold text-red-600 dark:text-red-400">
                    {report.failedRows || 0}
                  </p>
                </div>
                <div className="p-4 bg-yellow-100 rounded-xl dark:bg-yellow-500/15">
                  <p className="text-sm text-gray-500 dark:text-slate-400">Duplicates</p>
                  <p className="text-xl font-semibold text-yellow-600 dark:text-yellow-400">
                    {report.duplicateEmail || 0}
                  </p>
                </div>
              </div>

              {/* Error details table */}
              {report?.validateError && report.validateError.length > 0 && (
                <div className="w-full max-w-2xl mt-6">
                  <h4 className="text-lg font-semibold mb-2 dark:text-slate-100">
                    Validation Errors
                  </h4>
                  <div className="max-h-60 overflow-y-auto border rounded-lg dark:border-slate-700">
                    <table className="w-full text-sm text-left dark:text-slate-200">
                      <thead className="bg-gray-100 sticky top-0 dark:bg-slate-800 dark:text-slate-100">
                        <tr>
                          <th className="p-2 font-semibold">Row</th>
                          <th className="p-2 font-semibold">Email</th>
                          <th className="p-2 font-semibold">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.validateError.map((err, index) => (
                          <tr key={index} className="border-b dark:border-slate-700">
                            <td className="p-2">{err.row}</td>
                            <td className="p-2">{err.email}</td>
                            <td className="p-2 text-red-600 dark:text-red-400">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <ButtonComponent
                onClick={() => {
                  setStep(1);
                  setFile(null);
                  setColumns([]);
                  setMappedColumns({});
                  setPreview([]);
                }}
              >
                Import More Data
              </ButtonComponent>
            </motion.div>
          )}
        </div>
      </Card>
    </div>
  );
}
